import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronUp, Clock, Filter, Sparkles, Star, Target, TrendingUp, Users } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from '../components/GlobalPhoebeChat';
type Priority = 'critical' | 'warning' | 'positive';
type Insight = {
  id: number;
  skill: string;
  headline: string;
  priority: Priority;
  affectedLearners: number;
  metrics: {
    label: string;
    value: string;
  }[];
  signals: string[];
  actions: string[];
  timestamp: string;
  learner?: string;
};
const insights: Insight[] = [{
  id: 1,
  skill: 'CSS Fundamentals',
  headline: '50% first-attempt failure rate — below the 70% pass threshold',
  priority: 'critical',
  affectedLearners: 10,
  metrics: [{
    label: 'Failing',
    value: '10 of 20 learners'
  }, {
    label: 'Average score',
    value: '54%'
  }, {
    label: 'Pass threshold',
    value: '70%'
  }],
  signals: ['Questions 4–7 are most missed', 'High confidence with low accuracy', 'Average completion is only 12 minutes'],
  actions: ['Email the 10 learners who are failing', 'Schedule a group study session'],
  timestamp: 'Detected 2 hours ago'
}, {
  id: 2,
  skill: 'JavaScript Basics',
  headline: '34% misinformation rate on async/await concepts — 2.8× above average',
  priority: 'critical',
  affectedLearners: 89,
  metrics: [{
    label: 'Learners affected',
    value: '89'
  }, {
    label: 'Misinformation rate',
    value: '34%'
  }, {
    label: 'Attempts analyzed',
    value: '156'
  }],
  signals: ['Question 6 has the highest error rate', 'Confident but incorrect responses are increasing', '67% abandon the question mid-attempt'],
  actions: ['Host an async/await Q&A', 'Share a visual event-loop guide', 'Review question phrasing'],
  timestamp: 'Detected today'
}, {
  id: 3,
  skill: 'Responsive Design',
  headline: 'Enrollment is at 12% despite being a core learning requirement',
  priority: 'warning',
  affectedLearners: 218,
  metrics: [{
    label: 'Enrolled',
    value: '30 of 248'
  }, {
    label: 'Started this month',
    value: '8'
  }, {
    label: 'Completion rate',
    value: '63%'
  }],
  signals: ['Learners cite unclear prerequisites', 'The skill title is perceived as intimidating', 'Interest drops after the first preview'],
  actions: ['Send a targeted enrollment message', 'Clarify prerequisites', 'Offer a short orientation session'],
  timestamp: 'Detected yesterday'
}, {
  id: 4,
  skill: 'HTML Basics',
  headline: 'First-attempt pass rate rose 23% after supplemental videos were added',
  priority: 'positive',
  affectedLearners: 124,
  metrics: [{
    label: 'Pass rate',
    value: '87%'
  }, {
    label: 'Change',
    value: '+23%'
  }, {
    label: 'Average completion',
    value: '3.1 attempts'
  }],
  signals: ['Videos are mentioned in 78% of positive feedback', 'Completion time decreased by 26%', 'Strong gains across all learner groups'],
  actions: ['Share this success with the content team', 'Reuse the video pattern in CSS', 'Recognize high performers'],
  timestamp: 'Observed over 3 weeks'
}, {
  id: 5,
  skill: 'Multiple Skills',
  headline: 'Alex Rivera is consistently scoring below 50% across five attempts',
  priority: 'warning',
  affectedLearners: 1,
  learner: 'Alex Rivera',
  metrics: [{
    label: 'Average score',
    value: '45%'
  }, {
    label: 'Attempts',
    value: '5'
  }, {
    label: 'Last activity',
    value: '3 days ago'
  }],
  signals: ['Foundational gaps across modules', 'High misinformation indicator', 'Scores are not improving on retakes'],
  actions: ['Schedule a 1-on-1 check-in', 'Assess learning barriers', 'Create a personalized support plan'],
  timestamp: 'Detected 3 days ago'
}, {
  id: 6,
  skill: 'Organization Overview',
  headline: 'Learning participation is gaining momentum',
  priority: 'positive',
  affectedLearners: 198,
  metrics: [{
    label: 'Monthly participation',
    value: '80%'
  }, {
    label: 'Change',
    value: '+20% vs 30 days ago'
  }, {
    label: 'Learners active this month',
    value: '198 of 248'
  }],
  signals: ['Monthly participation has reached 80% of learners.', 'Learning activity is up 20% compared with the prior 30-day period.', 'A timely reminder can help convert active learners into completed modules.'],
  actions: ['Send a completion reminder to active learners', 'Identify learners close to completing a module', 'Review active learner progress by skill'],
  timestamp: 'Observed this month'
}, {
  id: 7,
  skill: 'Organization Overview',
  headline: 'Completion needs a focused follow-up',
  priority: 'warning',
  affectedLearners: 223,
  metrics: [{
    label: 'Completed all modules',
    value: '10%'
  }, {
    label: 'Change',
    value: '-20% vs 30 days ago'
  }, {
    label: 'Learners needing follow-up',
    value: '223 of 248'
  }],
  signals: ['Only 10% of learners have completed every skill module.', 'Completion fell 20% from the prior 30-day period.', 'Active learners with one or two modules remaining are the strongest follow-up opportunity.'],
  actions: ['Send a targeted completion reminder', 'Identify learners with one or two modules remaining', 'Schedule a completion support check-in'],
  timestamp: 'Detected this month'
}, {
  id: 8,
  skill: 'CSS Fundamentals',
  headline: 'CSS Fundamentals is a priority support opportunity',
  priority: 'critical',
  affectedLearners: 10,
  metrics: [{
    label: 'Misinformation rate',
    value: '22%'
  }, {
    label: 'Support focus',
    value: 'Flexbox, Grid, positioning'
  }, {
    label: 'Priority',
    value: 'Above other core modules'
  }],
  signals: ['CSS Fundamentals has a 22% misinformation rate, above the other core modules.', 'Flexbox, Grid, and positioning need clearer guided practice.', 'Strengthening these concepts before the next assessment can improve learner readiness.'],
  actions: ['Share targeted CSS practice resources', 'Run a Flexbox and Grid support session', 'Review positioning questions for clarity'],
  timestamp: 'Detected today'
}, {
  id: 9,
  skill: 'Assessment Readiness',
  headline: 'First-attempt readiness needs attention',
  priority: 'critical',
  affectedLearners: 248,
  metrics: [{
    label: 'Average first-attempt score',
    value: '64.93%'
  }, {
    label: 'Pass threshold',
    value: '70%'
  }, {
    label: 'Readiness gap',
    value: '5.07 percentage points'
  }],
  signals: ['Average first-attempt score is 64.93%, below the 70% pass threshold.', 'Confident incorrect responses point to concepts that need intervention before another full assessment.', 'Targeted pre-assessment practice can strengthen readiness and reduce unnecessary retakes.'],
  actions: ['Identify concepts driving confident incorrect answers', 'Share a targeted pre-assessment review', 'Offer a short readiness check before retakes'],
  timestamp: 'Detected today'
}];
const learners = ['All Learners', 'Alex Rivera', 'Emily Davis', 'John Smith', 'Lisa Wong', 'Michael Chen', 'Sarah Johnson'];
export function InsightsActionsPage() {
  const location = useLocation();
  const [priority, setPriority] = useState<'all' | Priority>('all');
  const [skill, setSkill] = useState('All Skills');
  const [learner, setLearner] = useState('All Learners');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const skills = useMemo(() => ['All Skills', ...Array.from(new Set(insights.map(item => item.skill))).sort()], []);
  useEffect(() => {
    const insightId = Number(new URLSearchParams(location.search).get('insight'));
    if (!insights.some(insight => insight.id === insightId)) return;
    setPriority('all');
    setSkill('All Skills');
    setLearner('All Learners');
    setExpanded(new Set([insightId]));
    window.requestAnimationFrame(() => {
      document.getElementById(`insight-${insightId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });
  }, [location.search]);
  const scoped = insights.filter(item => learner === 'All Learners' ? !item.learner : item.learner === learner);
  const visible = scoped.filter(item => (priority === 'all' || item.priority === priority) && (skill === 'All Skills' || item.skill === skill));
  const counts = {
    critical: scoped.filter(item => item.priority === 'critical').length,
    warning: scoped.filter(item => item.priority === 'warning').length,
    positive: scoped.filter(item => item.priority === 'positive').length
  };
  const toggleInsight = (insight: Insight) => {
    setExpanded(current => {
      const next = new Set(current);
      next.has(insight.id) ? next.delete(insight.id) : next.add(insight.id);
      return next;
    });
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-2192" data-mp="36fjdoqj3kixm">
      <div className="border-b border-gray-200 bg-white" data-id="element-2193" data-mp="3l57dfnbmr9j8">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8" data-id="element-2194" data-mp="3l4nvw7pw6n5q">
          <Link to="/admin" className="inline-flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900" data-id="element-2195" data-mp="3g6w37bhfax0h">
            <ArrowLeft size={16} data-id="element-2196" data-mp="2njt7ss81n2eg" /> Admin Hub
          </Link>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#30718D]" data-id="element-2197" data-mp="32fh7q56bx3cj">
            <Sparkles size={16} data-id="element-2198" data-mp="2xetklul816nl" /> AI insights
          </span>
        </div>
      </div>
      <main className="mx-auto max-w-[1440px] px-4 py-7 sm:px-6 lg:px-8" data-id="element-2199" data-mp="hnp4tdo19yhr">
        <header data-phoebe-tour="insights" className="mb-6" data-id="element-2200" data-mp="cpnnax69a78e">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl" data-id="element-2201" data-mp="cp42n8p814qo">
            Insights &amp; Actions
          </h1>
          <p className="mt-1 text-sm text-gray-600" data-id="element-2202" data-mp="p7zsdk8uqzsr">
            AI-powered analysis of learning patterns across your organization.
          </p>
        </header>

        <section aria-label="Insight summary" className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4" data-id="element-2203" data-mp="b9t3gtlvsa58">
          <StatCard label="Skills monitored" value={String(new Set(scoped.map(item => item.skill)).size)} icon={<Target size={20} data-id="element-2205" data-mp="17xfxylze9ig1" />} color="text-[#30718D]" isActive={priority === 'all' && skill === 'All Skills'} onClick={() => {
          setPriority('all');
          setSkill('All Skills');
        }} data-id="element-2204" data-mp="12zefo39sln5m" />
          <StatCard label="Critical issues" value={String(counts.critical)} icon={<AlertTriangle size={20} data-id="element-2207" data-mp="1qoqm4vdqjh48" />} color="text-[#DB3725]" isActive={priority === 'critical' && skill === 'All Skills'} onClick={() => {
          setPriority('critical');
          setSkill('All Skills');
        }} data-id="element-2206" data-mp="1lyijmxmh8qqf" />
          <StatCard label="Improvement opportunities" value={String(counts.warning)} icon={<TrendingUp size={20} data-id="element-2209" data-mp="1mlg6p35v1vkx" />} color="text-[#b77808]" isActive={priority === 'warning' && skill === 'All Skills'} onClick={() => {
          setPriority('warning');
          setSkill('All Skills');
        }} data-id="element-2208" data-mp="1whjasek9w4ai" />
          <StatCard label="Success stories" value={String(counts.positive)} icon={<Star size={20} data-id="element-2211" data-mp="18rf39w3yrke8" />} color="text-[#007558]" isActive={priority === 'positive' && skill === 'All Skills'} onClick={() => {
          setPriority('positive');
          setSkill('All Skills');
        }} data-id="element-2210" data-mp="1cta8hbp0mhxa" />
        </section>

        <section aria-label="Filter insights" className="mb-5 flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm lg:flex-row lg:items-center" data-id="element-2212" data-mp="tylokcwin8am">
          <div className="flex flex-wrap gap-1 rounded-lg bg-gray-50 p-1" role="group" aria-label="Priority filter" data-id="element-2213" data-mp="otrn3walpdh7">
            {([['all', 'All'], ['critical', 'Critical'], ['warning', 'Warning'], ['positive', 'Positive']] as const).map(([value, label]) => <button key={value} type="button" onClick={() => setPriority(value)} className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${priority === value ? value === 'critical' ? 'bg-[#DB3725] text-white' : value === 'warning' ? 'bg-[#F59E0B] text-white' : value === 'positive' ? 'bg-[#007558] text-white' : 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-white hover:text-gray-900'}`} data-id="element-2214" data-mp="b0a6okd7ogve">
                {label}
                {value !== 'all' && ` (${counts[value]})`}
              </button>)}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto" data-id="element-2215" data-mp="1ypy4i74a0xao">
            <SelectFilter label="Skill" value={skill} options={skills} onChange={setSkill} data-id="element-2216" data-mp="1tyy7z2z2kh8p" />
            <SelectFilter label="Learner" value={learner} options={learners} onChange={value => {
            setLearner(value);
            setPriority('all');
          }} data-id="element-2217" data-mp="27ojlvyy0i69v" />
            <button type="button" onClick={() => {
            setPriority('all');
            setSkill('All Skills');
            setLearner('All Learners');
          }} disabled={priority === 'all' && skill === 'All Skills' && learner === 'All Learners'} className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400" data-id="element-2218" data-mp="2coxsdcjyt85s">
              Reset
            </button>
          </div>
        </section>

        <section className="space-y-3" aria-live="polite" data-id="element-2219" data-mp="2g854r9xol1lw">
          <AnimatePresence mode="popLayout" data-id="element-2220" data-mp="21acqel3vej9a">
            {visible.map((insight, index) => <motion.article id={`insight-${insight.id}`} key={insight.id} layout initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            delay: index * 0.04
          }} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-2221" data-mp="268xnwcrqmou1" data-mpi={index}>
                <button type="button" onClick={() => toggleInsight(insight)} aria-expanded={expanded.has(insight.id)} className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50 sm:p-5" data-id="element-2222" data-mp="3l7lftp5ct475">
                  <PriorityIcon priority={insight.priority} data-id="element-2223" data-mp="bzsivgwws17f" />
                  <span className="min-w-0 flex-1" data-id="element-2224" data-mp="80a12u0dw68t">
                    <span className="mb-1 block text-sm font-semibold text-gray-900" data-id="element-2225" data-mp="7zi53i2vk6i0">
                      {insight.skill}
                      {insight.learner ? ` · ${insight.learner}` : ''}
                    </span>
                    <span className="block text-sm leading-relaxed text-gray-700" data-id="element-2226" data-mp="2ido23ilhjdzq">
                      {insight.headline}
                    </span>
                    <span className="mt-2 flex items-center gap-1 text-xs text-gray-500" data-id="element-2227" data-mp="2ndk7nm9tzdhe">
                      <Clock size={12} data-id="element-2228" data-mp="2rdb1xzykgbqo" /> {insight.timestamp}
                    </span>
                  </span>
                  {expanded.has(insight.id) ? <ChevronUp size={18} className="mt-1 flex-none text-gray-500" data-id="element-2229" data-mp="31cjxuyczs0fo" /> : <ChevronDown size={18} className="mt-1 flex-none text-gray-500" data-id="element-2230" data-mp="369imx2hsth70" />}
                </button>
                <AnimatePresence initial={false} data-id="element-2231" data-mp="3f35eshivy0ax">
                  {expanded.has(insight.id) && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2232" data-mp="3k4uq7ya0sfzj">
                      <div className="border-t border-gray-100 px-4 pb-5 pt-4 sm:px-5" data-id="element-2233" data-mp="34ytsyhzenbgc">
                        <div className="grid gap-3 sm:grid-cols-3" data-id="element-2234" data-mp="2h8zha3qtnsxo">
                          {insight.metrics.map(metric => <div key={metric.label} className="rounded-lg bg-gray-50 p-3" data-id="element-2235" data-mp="2c7jxef0drsgv">
                              <p className="text-xs font-medium text-gray-500" data-id="element-2236" data-mp="2l8rrekvv6755">
                                {metric.label}
                              </p>
                              <p className="mt-1 text-sm font-bold text-gray-900" data-id="element-2237" data-mp="1thu3du4fzaq0">
                                {metric.value}
                              </p>
                            </div>)}
                        </div>
                        <div className="mt-4" data-id="element-2238" data-mp="1o7hyrw79gnt3">
                          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-2239" data-mp="19htt6is1r2zk">
                            What the data shows
                          </h2>
                          <ul className="mt-2 space-y-2" data-id="element-2240" data-mp="224wt9fcdflrx">
                            {insight.signals.map(signal => <li key={signal} className="flex gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm text-gray-700" data-id="element-2241" data-mp="273815kj3vm7i">
                                <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#30718D]" data-id="element-2242" data-mp="1x868wftja40p" />
                                {signal}
                              </li>)}
                          </ul>
                        </div>
                        <div className="mt-4 border-t border-gray-100 pt-4" data-id="element-2243" data-mp="3hsdw6098qqfb">
                          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-2244" data-mp="3mpw17fk50li1">
                            Suggested actions
                          </h2>
                          <div className="mt-2 flex flex-wrap gap-2" data-id="element-2245" data-mp="3dpr541ke99j6">
                            {insight.actions.map(action => <button key={action} type="button" onClick={() => openPhoebeChat({
                        prompt: `Help me plan this action for ${insight.skill}: ${action}.`,
                        response: actionResponse(insight, action),
                        ...actionFollowUps(action)
                      })} className="rounded-lg border border-[#30718D]/30 bg-[#30718D]/5 px-3 py-2 text-left text-xs font-semibold text-[#30718D] transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2246" data-mp="2yzthbmqdmlui">
                                {action}
                              </button>)}
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.article>)}
          </AnimatePresence>
          {visible.length === 0 && <div className="rounded-xl border border-gray-100 bg-white px-6 py-12 text-center shadow-sm" data-id="element-2247" data-mp="2p5e2omiduijj">
              <Filter size={36} className="mx-auto mb-3 text-gray-300" data-id="element-2248" data-mp="334vxbiid0q0t" />
              <h2 className="text-base font-semibold text-gray-900" data-id="element-2249" data-mp="3332phzw0fzr8">
                No insights match these filters
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-2250" data-mp="367a4x9l00tas">
                Try choosing a different learner, skill, or priority.
              </p>
              <button type="button" onClick={() => {
            setPriority('all');
            setSkill('All Skills');
            setLearner('All Learners');
          }} className="mt-4 rounded-lg px-3 py-2 text-sm font-semibold text-[#30718D] hover:bg-[#30718D]/10" data-id="element-2251" data-mp="3b6d2ya2k9z99">
                Clear filters
              </button>
            </div>}
        </section>
      </main>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  color,
  isActive,
  onClick
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} aria-pressed={isActive} aria-label={`Show ${label.toLowerCase()}`} className={`rounded-xl border bg-white p-4 text-left shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${isActive ? 'border-[#30718D] ring-1 ring-[#30718D]/20' : 'border-gray-100 hover:border-[#30718D]/40 hover:bg-[#30718D]/[0.02]'}`} data-id="element-2252" data-mp="24izbm9d0lzv7">
      <span className={`flex items-center justify-between ${color}`} data-id="element-2253" data-mp="hqp3sxhahvuy">
        {icon}
        <span className="text-2xl font-bold text-gray-900" data-id="element-2254" data-mp="crvnkxambqr3">{value}</span>
      </span>
      <span className="mt-2 block text-xs font-medium text-gray-600" data-id="element-2255" data-mp="19jg0ta6srol9">
        {label}
      </span>
    </button>;
}
function PriorityIcon({
  priority
}: {
  priority: Priority;
}) {
  const classes = priority === 'critical' ? 'bg-[#DB3725]/10 text-[#DB3725]' : priority === 'warning' ? 'bg-[#F59E0B]/10 text-[#b77808]' : 'bg-[#007558]/10 text-[#007558]';
  const Icon = priority === 'positive' ? Star : priority === 'warning' ? TrendingUp : AlertTriangle;
  return <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${classes}`} data-id="element-2256" data-mp="3gd1knrosyy0f">
      <Icon size={18} data-id="element-2257" data-mp="1cspf5rqt7vem" />
    </span>;
}
function SelectFilter({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const Icon = label === 'Skill' ? Target : Users;
  const widthClass = label === 'Skill' ? 'sm:w-64' : 'sm:w-48';
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  return <div className={`relative w-full ${widthClass}`} ref={dropdownRef} data-id="element-2258" data-mp="6ezt83ltph7l">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} aria-haspopup="listbox" className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2259" data-mp="bd1caoj91gj2">
        <Icon size={18} className="flex-none text-[#30718D]" data-id="element-2260" data-mp="7uc2e5isgxjb" />
        <span className="flex-1 whitespace-nowrap text-left text-gray-700" data-id="element-2261" data-mp="3g8udkg410t75">
          {value}
        </span>
        <ChevronDown size={16} className={`flex-none text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-2262" data-mp="3c921htffc4x1" />
      </button>
      {isOpen && <div role="listbox" aria-label={`${label} filter`} className="absolute left-0 z-20 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-2263" data-mp="3l871l46mw6bb">
          {options.map(option => <button key={option} type="button" role="option" aria-selected={option === value} onClick={() => {
        onChange(option);
        setIsOpen(false);
      }} className={`w-full border-b border-gray-100 px-4 py-3 text-left text-sm transition-colors last:border-b-0 ${option === value ? 'bg-[#30718D]/5 font-semibold text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2264" data-mp="2shyq1v6lsk5p">
              {option}
            </button>)}
        </div>}
    </div>;
}
function buildBriefing(insight: Insight) {
  return `${insight.skill} needs attention.\n\n${insight.headline}\n\nKey signals:\n${insight.signals.map(signal => `• ${signal}`).join('\n')}\n\nI can help you choose the best next action for the ${insight.affectedLearners} affected learner${insight.affectedLearners === 1 ? '' : 's'}.`;
}
function actionResponse(insight: Insight, action: string) {
  if (action === 'Email the 10 learners who are failing') {
    return `10 of 20 learners are currently failing CSS Fundamentals. Their shared gaps are Flexbox, Grid, and positioning.\n\nLearners to contact:\n• Ava Morgan\n• Daniel Kim\n• Elena Rodriguez\n• Jordan Lee\n• Marcus Bennett\n• Maya Patel\n• Noah Williams\n• Priya Shah\n• Sofia Nguyen\n• Tyler Brooks\n\nWould you like me to compose a supportive email for these 10 learners with targeted resources and an invitation to the next group study session?`;
  }
  return `Here’s a practical plan for “${action}” in ${insight.skill}:\n\n1. Start with the learners and concepts most affected: ${insight.signals[0].toLowerCase()}.\n2. Set a focused 30-minute support touchpoint this week.\n3. Share one targeted resource, then check progress within 7 days.\n\nSuccess measure: improve the current ${insight.metrics[1]?.label.toLowerCase() || 'performance metric'} from ${insight.metrics[1]?.value || 'its current level'} while reducing the core struggle pattern.`;
}
function actionFollowUps(action: string) {
  if (action !== 'Email the 10 learners who are failing') return {};
  return {
    suggestedQuestions: ['Yes, compose the email', 'Show the recipient list', 'Use a more supportive tone', 'Not now'],
    suggestionResponses: {
      'Yes, compose the email': `Here’s a draft you can review before sending:\n\nSubject: Extra support for CSS Fundamentals\n\nHi there,\n\nI noticed CSS Fundamentals has been challenging so far. I’ve gathered short resources on Flexbox, Grid, and positioning, and I’d like to invite you to our next group study session.\n\nWould you like me to personalize this draft with the study-session time before you send it?`,
      'Show the recipient list': `The email will go to these 10 learners:\n\n• Ava Morgan\n• Daniel Kim\n• Elena Rodriguez\n• Jordan Lee\n• Marcus Bennett\n• Maya Patel\n• Noah Williams\n• Priya Shah\n• Sofia Nguyen\n• Tyler Brooks`,
      'Use a more supportive tone': `I’ll make the outreach encouraging and specific: acknowledge that CSS can take practice, highlight the available resources, and frame the study session as optional extra support rather than a remediation requirement. Would you like me to draft that version?`,
      'Not now': `No problem — I’ll leave the learner list and suggested resources ready for when you’re ready to follow up.`
    }
  };
}